import type { DriveAuthSession } from '../types';

export interface CollectionMeta {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  itemCount: number;
}

const authHeaders = (session: DriveAuthSession) => ({
  Authorization: `Bearer ${session.idToken || session.accessToken}`,
  'x-access-token': session.accessToken,
  'Content-Type': 'application/json',
});

const parseError = async (res: Response, fallback: string): Promise<string> => {
  const body = await res.json().catch(() => null);
  return body?.message || body?.error || fallback;
};

export const collectionsService = {
  async list(session: DriveAuthSession): Promise<{ collections: CollectionMeta[]; enabled: boolean; limit: number }> {
    const res = await fetch('/api/collections', {
      method: 'GET',
      headers: authHeaders(session),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Failed to load collections.'));
    }
    const data = await res.json();
    return {
      collections: Array.isArray(data?.collections) ? data.collections : [],
      enabled: Boolean(data?.enabled),
      limit: Number.isFinite(data?.limit) ? data.limit : 0,
    };
  },

  async create(session: DriveAuthSession, name: string): Promise<CollectionMeta> {
    const res = await fetch('/api/collections', {
      method: 'POST',
      headers: authHeaders(session),
      body: JSON.stringify({ name }),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Failed to create collection.'));
    }
    const data = await res.json();
    return data.collection;
  },

  async rename(session: DriveAuthSession, id: string, name: string): Promise<CollectionMeta> {
    const res = await fetch('/api/collections', {
      method: 'PUT',
      headers: authHeaders(session),
      body: JSON.stringify({ id, name }),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Failed to rename collection.'));
    }
    const data = await res.json();
    return data.collection;
  },

  async remove(session: DriveAuthSession, id: string): Promise<void> {
    const res = await fetch('/api/collections', {
      method: 'DELETE',
      headers: authHeaders(session),
      body: JSON.stringify({ id }),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Failed to delete collection.'));
    }
  },

  async updateItems(
    session: DriveAuthSession,
    collectionId: string,
    fileIds: string[],
    action: 'add' | 'remove',
  ): Promise<number> {
    const res = await fetch('/api/collections/items', {
      method: 'POST',
      headers: authHeaders(session),
      body: JSON.stringify({ collectionId, fileIds, action }),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Failed to update collection items.'));
    }
    const data = await res.json();
    return Number(data?.updated) || 0;
  },
};
